import React from 'react';
import PropTypes from 'prop-types';
import { ThemeProvider, css } from 'styled-components';
import { SERIF, SANS_SERIF } from './Webfonts';

/**
 * These styles facilitate sharing theme information between
 * our legacy components like the Insights pages,
 * as well as facilitating some layout features not supported
 * by Grommet.
 */

const FONTS = {
  sans: `${SANS_SERIF}, Helvetica, sans-serif`,
  serif: `${SERIF}, Georgia, serif`,
};

const GRID = {
  xsmall: {
    columns: 4,
    outerGutter: 16,
    gutter: 16,
    verticalPadding: 20,
  },
  small: {
    columns: 4,
    outerGutter: 28,
    gutter: 16,
    verticalPadding: 20,
  },
  medium: {
    columns: 8,
    outerGutter: 28,
    gutter: 12,
    verticalPadding: 20,
  },
  large: {
    columns: 12,
    outerGutter: 48,
    gutter: 24,
    verticalPadding: 20,
  },
  xlarge: {
    columns: 12,
    outerGutter: 100,
    gutter: 24,
    verticalPadding: 20,
  },
};

const BREAKPOINTS = {
  xsmall: 320, // not real
  small: 375,
  medium: 768,
  large: 1024,
  xlarge: 1400,
};

// Below here it's mostly functions, nothing to configure.

const LAYOUT_QUERY_HELPERS = {
  // Helpers to generate media queries relative a breakpoint.
  above: (bp) => `(min-width: ${bp}px)`,
  below: (bp) => `(max-width: ${bp - 1}px)`,
  between: (low, high) =>
    `(min-width: ${low}px) and (max-width: ${high - 1}px)`,
};

const LAYOUTS = Object.keys(BREAKPOINTS)
  // Generates media queries for all breakpoints
  .map((breakpointKey, breakpointIndex, keys) => {
    // For each breakpoint...
    const queries = {
      // e.g. above_small, below_medium
      [`above_${breakpointKey}`]: LAYOUT_QUERY_HELPERS.above(
        BREAKPOINTS[breakpointKey],
      ),
      [`below_${breakpointKey}`]: LAYOUT_QUERY_HELPERS.below(
        BREAKPOINTS[breakpointKey],
      ),
    };

    // Make "only" or "between" queries
    // ..which need some logic for the narrowest and widest breakpints
    if (breakpointIndex === 0) {
      // Narrowest breakpoint "only" is the same as min
      queries[`only_${breakpointKey}`] = queries[`below_${breakpointKey}`];
      queries.only_narrowest = queries[`below_${breakpointKey}`];
    } else if (breakpointIndex === keys.length - 1) {
      // Widest breakpoint
      // "only" is the same as max
      queries[`only_${breakpointKey}`] = queries[`above_${breakpointKey}`];
      queries.only_widest = queries[`above_${breakpointKey}`];
    } else {
      // Something in between
      queries[`only_${breakpointKey}`] = LAYOUT_QUERY_HELPERS.between(
        BREAKPOINTS[breakpointKey],
        BREAKPOINTS[keys[breakpointIndex + 1]],
      );
    }
    return queries;
  })
  .reduce(
    (acc, cv) =>
      // turn it back into an obj
      ({ ...acc, ...cv }),
    {},
  );

const LAYOUT_HELPERS = {
  LayoutContainer: () => css`
    width: 100%;
    margin: 0 auto;
    padding-left: var(--outerGutter);
    padding-right: var(--outerGutter);
    @media ${LAYOUTS.only_widest} {
      max-width: var(--breakpointWidth);
    }
  `,
  FullWidthContainer: () => css`
    width: calc(100% + var(--outerGutter) * 2);
    margin-left: calc(var(--outerGutter) * -1);
    margin-right: calc(var(--outerGutter) * -1);
  `,
  FixedLayoutContainer: () => css`
    width: 100%;
    margin: 0 auto;
    padding-left: var(--outerGutter);
    padding-right: var(--outerGutter);
    max-width: var(--layoutFixedMaxWidth);
  `,
  FlexGridContainer: () => css`
    display: flex;
    width: calc(100% + var(--gutters));
    height: calc(100% + var(--gutters));
    margin: calc(var(--gutters) * -0.5);
  `,
  FlexGridItem: () => css`
    flex: 0 0 auto;
    padding: calc(var(--gutters) * 0.5);
  `,
};

const STYLE_HELPERS = {
  pxToRem: (px) => {
    const REM_BASE =
      typeof window !== 'undefined'
        ? parseInt(
            window
              .getComputedStyle(document.documentElement)
              .getPropertyValue('font-size'),
            10,
          )
        : 16;
    return `${px / REM_BASE}rem`;
  },
};

export const CustomTheme = {
  type: { ...FONTS },
  breakpoints: { ...BREAKPOINTS },
  layouts: { ...LAYOUTS },
  grid: { ...GRID },
  helpers: { ...LAYOUT_HELPERS, ...STYLE_HELPERS },
};

export const SetThemeCSSVariables = () =>
  // Any layout value that changes over breakpoints gets stored as a css var so we don't need to worry about MQs when we actually apply them in a layout.
  Object.keys(CustomTheme.breakpoints).map(
    (breakpoint) =>
      // Generates a media query with the appropriate values for each breakpoint.
      `
      @media ${LAYOUTS[`above_${breakpoint}`]} {
        --columns: ${GRID[breakpoint].columns};
        --gutters: ${GRID[breakpoint].gutter}px;
        --outerGutter: ${GRID[breakpoint].outerGutter}px;
        --verticalPadding: ${GRID[breakpoint].verticalPadding}px;
        --breakpointWidth: ${BREAKPOINTS[breakpoint]}px;
        --layoutFixedMaxWidth: ${
          BREAKPOINTS[breakpoint] - GRID[breakpoint].outerGutter * 2
        }px;
      }
    `,
  );

const CustomThemeProvider = ({ children }) => (
  <ThemeProvider theme={CustomTheme}>{children}</ThemeProvider>
);

CustomThemeProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default CustomThemeProvider;
